import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';

// Date of birth picker modelled on Adifund's ScrollDateField: tapping the field
// opens a modal with Month / Day / Year scroll wheels and a Done button, which
// is much easier on a phone than paging a month calendar back to the 1980s.

const ITEM_H = 40;
const WHEEL_H = 200;
const WHEEL_PAD = (WHEEL_H - ITEM_H) / 2;

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const range = (from, to) => {
  const out = [];
  for (let i = from; i <= to; i += 1) out.push(i);
  return out;
};
const pad = (n) => String(n).padStart(2, '0');

// Same text the old react-datepicker field showed, so the filled field looks
// unchanged.
const formatValue = (d) =>
  d ? `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}` : '';

const formatLong = ({ y, m, d }) =>
  new Date(y, m, d).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

const ScrollWheel = ({ label, values, selected, onSelect, format, syncKey }) => {
  const ref = useRef(null);
  const settle = useRef(null);
  const syncing = useRef(false);
  const index = Math.max(0, values.indexOf(selected));

  // Keep the wheel on the selected value when it changes from outside (e.g.
  // the day list shrinking from 31 to 28). Keyed on the list's bounds, not the
  // array itself, which is rebuilt every render and would yank a wheel back
  // while another one is being scrolled.
  const first = values[0];
  const count = values.length;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    syncing.current = true;
    el.scrollTop = index * ITEM_H;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  }, [index, first, count, syncKey]);

  useEffect(() => () => clearTimeout(settle.current), []);

  const snap = useCallback(() => {
    const el = ref.current;
    if (!el || !values.length) return;
    const i = Math.max(
      0,
      Math.min(values.length - 1, Math.round(el.scrollTop / ITEM_H))
    );
    if (Math.abs(el.scrollTop - i * ITEM_H) > 0.5) {
      el.scrollTo({ top: i * ITEM_H, behavior: 'smooth' });
    }
    if (values[i] !== selected) onSelect(values[i]);
  }, [onSelect, selected, values]);

  const onScroll = () => {
    if (syncing.current) return;
    clearTimeout(settle.current);
    settle.current = setTimeout(snap, 100);
  };

  return (
    <div className="scroll-wheel">
      <div className="scroll-wheel__rail" aria-hidden="true" />
      <div
        ref={ref}
        role="listbox"
        aria-label={label}
        className="scroll-wheel__list"
        style={{ height: WHEEL_H }}
        onScroll={onScroll}
      >
        <div style={{ paddingTop: WHEEL_PAD, paddingBottom: WHEEL_PAD }}>
          {values.map((v) => (
            <div
              key={v}
              role="option"
              aria-selected={v === selected}
              className={`scroll-wheel__item${
                v === selected ? ' is-selected' : ''
              }`}
            >
              {format(v)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ScrollDatePicker = ({
  value,
  onChange,
  placeholderText = 'Select date',
  title = 'Date of Birth',
  hint = 'Scroll to choose your date of birth, then tap Done.',
  minYear = 1947,
  maxDate = new Date(),
  defaultDate = new Date(1990, 0, 1)
}) => {
  const [open, setOpen] = useState(false);
  // The wheels mount before the modal is laid out, when scrollTop cannot be
  // set yet; bumping this once it is on screen puts them on the chosen date.
  const [syncKey, setSyncKey] = useState(0);
  const toParts = (d) => ({ y: d.getFullYear(), m: d.getMonth(), d: d.getDate() });
  const [local, setLocal] = useState(() => toParts(value || defaultDate));

  const maxY = maxDate.getFullYear();
  const maxM = maxDate.getMonth();
  const maxD = maxDate.getDate();

  // Clamp to a real day that is not after maxDate.
  const clamp = ({ y, m, d }) => {
    const yy = Math.min(maxY, Math.max(minYear, y));
    const mm = yy === maxY ? Math.min(m, maxM) : m;
    let dd = Math.min(d, daysInMonth(yy, mm));
    if (yy === maxY && mm === maxM) dd = Math.min(dd, maxD);
    return { y: yy, m: mm, d: dd };
  };

  const openPicker = () => {
    setLocal(clamp(toParts(value || defaultDate)));
    setOpen(true);
  };

  const years = range(minYear, maxY);
  const months = range(0, local.y === maxY ? maxM : 11);
  const days = range(
    1,
    local.y === maxY && local.m === maxM
      ? maxD
      : daysInMonth(local.y, local.m)
  );

  const update = (patch) => setLocal((cur) => clamp({ ...cur, ...patch }));

  const done = () => {
    onChange(new Date(local.y, local.m, local.d));
    setOpen(false);
  };

  return (
    <>
      <div className="react-datepicker-wrapper">
        <div className="react-datepicker__input-container">
          <input
            type="text"
            readOnly
            className="custom_input scroll-date-trigger"
            placeholder={placeholderText}
            value={formatValue(value)}
            onClick={openPicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openPicker();
              }
            }}
          />
        </div>
      </div>

      <Modal
        show={open}
        onHide={() => setOpen(false)}
        centered
        className="scroll-date-modal"
        backdropClassName="scroll-date-backdrop"
        onEntering={() => setSyncKey((k) => k + 1)}
      >
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
          {/* Not Bootstrap's closeButton: the global `.modal-header .close`
              rules knock its × off centre. */}
          <button
            type="button"
            className="scroll-date-modal__close"
            aria-label="Close"
            onClick={() => setOpen(false)}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                d="M5 5l14 14M19 5L5 19"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body>
          <p className="scroll-date-modal__hint">{hint}</p>
          <div className="scroll-date-modal__box">
            <div className="scroll-date-modal__wheels">
              <ScrollWheel
                label="Month"
                syncKey={syncKey}
                values={months}
                selected={local.m}
                onSelect={(m) => update({ m })}
                format={(m) => MONTHS[m]}
              />
              <ScrollWheel
                label="Day"
                syncKey={syncKey}
                values={days}
                selected={local.d}
                onSelect={(d) => update({ d })}
                format={String}
              />
              <ScrollWheel
                label="Year"
                syncKey={syncKey}
                values={years}
                selected={local.y}
                onSelect={(y) => update({ y })}
                format={String}
              />
            </div>
            <div className="scroll-date-modal__preview" aria-live="polite">
              {formatLong(local)}
            </div>
          </div>
          <button
            type="button"
            className="scroll-date-modal__done"
            onClick={done}
          >
            Done
          </button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ScrollDatePicker;
